import { isNil } from 'lodash';
import { ServiceType } from '../../../global/api/models';
import {
    getApiHost,
    serviceRequest,
} from '../../../global/api/public/utils';
import { ResponseStatus } from '../../../global/models';
import { CAMPUS_ID_BUENOS_AIRES } from '../shared/js/constants';
import {
    Campaign,
    CampaignCommitment,
    HouseholdCampaignCommitments,
} from '../shared/models';

export const getCampaignCommitment = async (
    accessToken: string,
    campaignId: number,
): Promise<CampaignCommitment> => {
    const { data } = await serviceRequest({
        accessToken,
        expectedErrorTypes: [ResponseStatus.NOT_FOUND],
        method: 'GET',
        url: `${getApiHost(ServiceType.Core)}/api/v4/giving/campaigns/${campaignId}/commitments/mine`,
    });

    return data;
};

export const getCampaignCommitments = async (
    accessToken: string,
    useRockAPIs?: boolean,
): Promise<CampaignCommitment[]> => {
    // NOTE: No current Core API support for this commitment (get all of the context user's Commitments across multiple Campaigns).
    // We don't really have a current need.  The widget consuming this endpoint is not in production use.
    // We should maybe consider retiring it.
    // We'll have to think about where the widgets live in general, if and when we retire Healthy Church Admin UI entirely.

    const apiPath = useRockAPIs ? '/api/v4/campaign-commitments/mine' : '/api/campaign/goal/mine';

    const { data } = await serviceRequest({
        url: `${getApiHost(ServiceType.Giving)}${apiPath}`,
        method: 'GET',
        accessToken,
    });

    return data ?? [];
};

export const getCampaignCommitmentsByCampaignForHousehold = async (
    accessToken: string,
    campaignId: number,
): Promise<HouseholdCampaignCommitments> => {
    const { data } = await serviceRequest({
        accessToken,
        expectedErrorTypes: [ResponseStatus.NOT_FOUND],
        method: 'GET',
        url: `${getApiHost(ServiceType.Core)}/api/v4/giving/campaigns/${campaignId}/commitments/mine?includeSpouse=true`,
    });

    return data;
};

export const getCampaignDetails = async (
    campaignId: number,
): Promise<Campaign> => {
    const { data } = await serviceRequest({
        method: 'GET',
        url: `${getApiHost(ServiceType.Core)}/api/v4/giving/campaigns/${campaignId}`,
    });

    return data;
};

const saveCampaignCommitment = async (
    accessToken: string,
    commitment: CampaignCommitment,
    apiMethod: 'POST' | 'PUT',
    locale?: string,
    campusId?: number,
): Promise<CampaignCommitment> => {
    let url = commitment.id ?
        `${getApiHost(ServiceType.Core)}/api/v4/giving/campaigns/${commitment.campaignId}/commitments/mine` :
        `${getApiHost(ServiceType.Core)}/api/v4/giving/campaigns/commitments/mine`;

    // NOTE: HC back-end is presently very stupid about locales.  Sigh.
    // It requires that the locale be one of a specific set in order to pick out localized content
    // (e.g. in this case the template for the confirmation email for creating or updating a campaign commitment).
    // So, we're going to take less specific locales (coming from a consumer like 'Skinny' web, i.e. welcome.saddleback.com)
    // and pick out the best specific one supported by HC.
    if (!isNil(locale)) {
        let finaleLocale: string;

        switch (locale.toLowerCase()) {
            case 'de':
                finaleLocale = 'de-DE';
                break;
            case 'es':
                finaleLocale = campusId === CAMPUS_ID_BUENOS_AIRES ? 'es-AR' : 'es-US'; // doesn't really make a difference!  but, you know ... =)
                break;
            case 'zh-hans':
                finaleLocale = 'zh-Hans-HK';
                break;
            case 'zh-hant':
                finaleLocale = 'zh-Hant-HK';
                break;
            default:
                finaleLocale = locale;
        }

        url += `?culture=${finaleLocale}`;
    }

    const { data } = await serviceRequest({
        accessToken,
        data: commitment,
        method: apiMethod,
        url,
    });

    return data;
};

export const postCampaignCommitment = (
    accessToken: string,
    commitment: CampaignCommitment,
    locale?: string,
    campusId?: number,
): Promise<CampaignCommitment> => saveCampaignCommitment(accessToken, commitment, 'POST', locale, campusId);

export const putCampaignCommitment = (
    accessToken: string,
    commitment: CampaignCommitment,
    locale?: string,
    campusId?: number,
): Promise<CampaignCommitment> => saveCampaignCommitment(accessToken, commitment, 'PUT', locale, campusId);
